import { useState, useCallback } from 'react'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog'
import { mdiCog, mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { AppIcon } from './ui/icon'
import {
  usePropertyCategories,
  useCreatePropertyCategory,
  useDeletePropertyCategory,
  type PropertyCategory,
} from '../hooks/useQueries'
import { EmptyState } from './ui/empty-state'
import { useTranslation } from 'react-i18next'

interface CategoryManagementDialogProps {
  org: string
}

function getCategoryName(category: PropertyCategory): string {
  if (!category?.name) return category?.code || 'Unnamed'
  if (typeof category.name === 'object' && category.name !== null) {
    const names = category.name as Record<string, string>
    return names.en || names.fr || Object.values(names)[0] || category.code || 'Unnamed'
  }
  return String(category.name) || category.code || 'Unnamed'
}

export function CategoryManagementDialog({ org }: CategoryManagementDialogProps) {
  const { t } = useTranslation('types')
  const [isOpen, setIsOpen] = useState(false)
  const [nameEn, setNameEn] = useState('')
  const [nameFr, setNameFr] = useState('')
  const [code, setCode] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<PropertyCategory | null>(null)

  const { data: categories = [], isLoading } = usePropertyCategories(org)
  const createCategory = useCreatePropertyCategory(org)
  const deleteCategory = useDeletePropertyCategory(org)

  const handleCreate = useCallback(async () => {
    if (!nameEn.trim() || !code.trim()) return

    const name: Record<string, string> = { en: nameEn.trim() }
    if (nameFr.trim()) {
      name.fr = nameFr.trim()
    }

    try {
      await createCategory.mutateAsync({ code: code.trim(), name })
      setNameEn('')
      setNameFr('')
      setCode('')
    } catch (error) {
      console.error('Failed to create category:', error)
    }
  }, [nameEn, nameFr, code, createCategory])

  const handleDelete = useCallback(async () => {
    if (!deleteTarget?.uid) return

    try {
      await deleteCategory.mutateAsync(deleteTarget.uid)
      setDeleteTarget(null)
    } catch (error) {
      console.error('Failed to delete category:', error)
    }
  }, [deleteTarget, deleteCategory])

  const canCreate = nameEn.trim().length > 0 && code.trim().length > 0

  return (
    <>
      <Button
        variant="outline"
        onClick={() => setIsOpen(true)}
        data-testid="manage-categories-btn"
      >
        <AppIcon path={mdiCog} size="sm" className="mr-2" />
        Manage Categories
      </Button>

      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="right" className="sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Property Categories</SheetTitle>
            <SheetDescription>
              Manage categories to organize your property definitions
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-col gap-6 overflow-y-auto px-4 pb-4">
            {/* Create Form */}
            <div className="space-y-4 rounded-lg border border-border p-4" data-testid="create-category-btn">
              <h3 className="text-sm font-medium text-foreground">Create New Category</h3>
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <Label htmlFor="category-name-en" className="text-xs text-muted-foreground">
                    Name (English) <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="category-name-en"
                    value={nameEn}
                    onChange={(e) => setNameEn(e.target.value)}
                    placeholder="e.g. Financial"
                    data-testid="category-name-input"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="category-name-fr" className="text-xs text-muted-foreground">
                    Name (French)
                  </Label>
                  <Input
                    id="category-name-fr"
                    value={nameFr}
                    onChange={(e) => setNameFr(e.target.value)}
                    placeholder="e.g. Financier"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="category-code" className="text-xs text-muted-foreground">
                    Code <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="category-code"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="e.g. financial"
                    className="font-mono"
                    data-testid="category-code-input"
                  />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={!canCreate || createCategory.isPending}
                  className="w-full"
                  data-testid="save-category-btn"
                >
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                  {createCategory.isPending ? 'Creating...' : 'Create Category'}
                </Button>
              </div>
            </div>

            {/* Existing Categories */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">Existing Categories</h3>
              {isLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="motion-safe:animate-spin rounded-full h-5 w-5 border-b-2 border-primary mr-2"></div>
                  <span className="text-sm text-muted-foreground">Loading...</span>
                </div>
              ) : categories.length === 0 ? (
                <EmptyState variant="inline" className="py-4" title={t('categories.empty')} />
              ) : (
                <div className="space-y-2">
                  {categories.map((category) => (
                    <div
                      key={category.uid}
                      className="flex items-center justify-between rounded-md border border-border p-3"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground truncate">
                          {getCategoryName(category)}
                        </p>
                        <code className="text-xs text-muted-foreground bg-muted px-1.5 py-0.5 rounded">
                          {category.code}
                        </code>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive ml-2 shrink-0"
                        onClick={() => setDeleteTarget(category)}
                        data-testid={`delete-category-btn-${category.code}`}
                      >
                        <AppIcon path={mdiTrashCanOutline} size="sm" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the category "{deleteTarget ? getCategoryName(deleteTarget) : ''}"?
              Properties assigned to this category will become uncategorized.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-category-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-category-btn"
            >
              {deleteCategory.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
